import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface UseAcmBaseOptions {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  filters?: {
    status?: string;
    curso?: string;
    dataInicio?: string;
    dataFim?: string;
    acessoFiltro?: string;
    ordenacao?: 'recentes' | 'antigos';
    studentType?: 'all' | 'active' | 'loyal' | 'new';
  };
}

const DEFAULT_PAGE_SIZE = 20;

export function useAcmBase({ 
  page = 1, 
  pageSize = DEFAULT_PAGE_SIZE,
  searchTerm = '', 
  filters = {} 
}: UseAcmBaseOptions = {}) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [availableCourses, setAvailableCourses] = useState<string[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Busca cursos disponíveis
      const { data: cursosData } = await supabase
        .from('acm_base')
        .select('nome_curso')
        .not('nome_curso', 'is', null);

      const uniqueCursos = Array.from(new Set(
        cursosData?.map(record => record.nome_curso).filter(Boolean)
      )).sort();
      setAvailableCourses(uniqueCursos);

      // Primeiro, busca emails únicos para contagem
      let countQuery = supabase
        .from('acm_base')
        .select('email', { count: 'exact', head: true, distinct: true })
        .not('email', 'is', null);

      if (searchTerm) {
        countQuery = countQuery.or(
          `nome.ilike.%${searchTerm}%,` +
          `email.ilike.%${searchTerm}%,` +
          `documento.ilike.%${searchTerm}%`
        );
      }

      const { count } = await countQuery;
      setTotalCount(count || 0);

      // Busca os dados completos
      let query = supabase
        .from('acm_base')
        .select('*');

      // Aplica busca por texto
      if (searchTerm) {
        query = query.or(
          `nome.ilike.%${searchTerm}%,` +
          `email.ilike.%${searchTerm}%,` +
          `documento.ilike.%${searchTerm}%`
        );
      }

      // Aplica filtro de curso
      if (filters.curso && filters.curso !== 'todos') {
        query = query.eq('nome_curso', filters.curso);
      }

      // Aplica filtros especiais baseados no tipo de aluno
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      if (filters.studentType && filters.studentType !== 'all') {
        switch (filters.studentType) {
          case 'active':
            // Alunos que acessaram nos últimos 30 dias
            query = query.gte('ultimo_acesso', thirtyDaysAgo.toISOString());
            break;
          case 'loyal':
            // Alunos com mais de um curso
            query = query.not('nome_curso', 'is', null);
            break;
          case 'new':
            // Alunos cadastrados este mês
            query = query.gte('created_at', firstDayOfMonth.toISOString());
            break;
        }
      }

      // Aplica ordenação
      if (filters.ordenacao) {
        query = query.order('ultimo_acesso', { 
          ascending: filters.ordenacao === 'antigos',
          nullsLast: true
        });
      } else {
        query = query.order('email');
      }

      // Executa a query
      const { data: records, error: recordsError } = await query;

      if (recordsError) throw recordsError;

      // Agrupa registros por email
      const groupedData = {};
      records?.forEach(record => {
        if (!record.email) return;

        if (!groupedData[record.email]) {
          groupedData[record.email] = {
            email: record.email,
            nome: record.nome,
            documento: record.documento,
            telefone: record.telefone,
            ultimo_acesso: record.ultimo_acesso,
            created_at: record.created_at,
            cursos: []
          };
        }

        if (record.nome_curso) {
          groupedData[record.email].cursos.push({
            nome_curso: record.nome_curso,
            entrega: record.entrega,
            progresso: record.progresso,
            data_inicio: record.data_inicio,
            data_conclusao_prevista: record.data_conclusao_prevista
          });
        }
      });

      // Aplica filtros pós-agrupamento
      let filteredData = Object.values(groupedData);

      // Filtra alunos fiéis (com mais de um curso) após agrupamento
      if (filters.studentType === 'loyal') {
        filteredData = filteredData.filter(student => student.cursos.length > 1);
      }

      // Converte para array e aplica paginação
      const from = (page - 1) * pageSize;
      const to = from + pageSize;
      const paginatedData = filteredData
        .sort((a: any, b: any) => {
          // Mantém a ordenação por último acesso se especificada
          if (filters.ordenacao) {
            const aDate = a.ultimo_acesso ? new Date(a.ultimo_acesso) : new Date(0);
            const bDate = b.ultimo_acesso ? new Date(b.ultimo_acesso) : new Date(0);
            return filters.ordenacao === 'recentes' ? 
              bDate.getTime() - aDate.getTime() : 
              aDate.getTime() - bDate.getTime();
          }
          return a.email.localeCompare(b.email);
        })
        .slice(from, to);

      setData(paginatedData);

    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, JSON.stringify(filters)]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    totalCount,
    totalPages: Math.ceil(totalCount / pageSize),
    currentPage: page,
    pageSize,
    availableCourses,
    refresh: fetchData
  };
}